import {
  AgendaTranscriptMappingMode,
  MeetingMedium,
  MinutesAnonymization,
  MinutesAttribution,
  MinutesDateFormat,
  MinutesLanguage,
  MinutesNameFormat,
  MinutesPassive,
  MinutesTimeFormat,
  MinutesVerboseness,
  MinutingAgent,
} from './meetings.service'

/** The key source used to crypto-shred a meeting's artifacts. */
export type EncryptionKeySource = 'KMS'

/** Settings the client owns. */
export type ClientSettings = {
  allowSignUp: boolean
  minutesVerboseness: MinutesVerboseness
  minutesAnonymization: MinutesAnonymization
  minutesPassive: MinutesPassive
  minutesNameFormat: MinutesNameFormat
  minutesDateFormat: MinutesDateFormat
  minutesTimeFormat: MinutesTimeFormat
  minutesLanguage: MinutesLanguage
  minutesAttribution: MinutesAttribution
  anonymiseMemberSpeakers: boolean
  useTranscriptRefinement: boolean
  shareUrlOptOut: boolean
  transcriberInputLanguage: string
  transcriberOutputLanguage: string
  allowTranscriptDownload: boolean
  allowVideoDownload: boolean
  allowMinutesDownload: boolean
  allowOutlineDownload: boolean
  allowManualMeetingCreation: boolean
  allowMeetingUpload: boolean
  useVerificationDiscovery: boolean
  useAgendaTranscriptMapping: AgendaTranscriptMappingMode
  allowBoardPackUploads: boolean
  allowAdminMeetingActions: boolean
  allowRetentionExtensionRequests: boolean
  softDeletionPeriodInDays: number
  isIntercomEnabled: boolean
  isSharingEnabled: boolean
  isCalendarSynchronizationEnabled: boolean
  recordingType: 'VIDEO' | 'AUDIO'
  defaultMeetingMedium: MeetingMedium
  sendSoftDeletionWarningEmail: boolean
  softDeletionWarningHours: number
  sendSoftDeletionConfirmationEmail: boolean
  sendHardDeletionConfirmationEmail: boolean
  sendAssistantAccessDeniedEmail: boolean
  sendUpcomingMeetingReminderEmail: boolean
  sendWeeklyDigestEmail: boolean
  sendConsentNotification: boolean
  joinMeetingInternal: boolean
  joinMeetingExternal: boolean
  freezeJoinSettings: boolean
  useSectionHeadings: boolean
  usePartialMinutesRegeneration: boolean
  allowRestrictedUsers: boolean
  allowMeetingEntities: boolean
  allowActionItems: boolean
  allowLlmSpeechRecovery: boolean
  sessionTimeoutInMinutes: number
  dashboardUrl: string | null
  enabledMinutesLanguages: string[]
  minutingAgents: MinutingAgent[]
  useVirtualHostedStorageUrls: boolean
  contactEmails: string[]
  context: string[]
  assistantDisplayName: string | null
  llmModel: string | null
  encryptionKeySource: EncryptionKeySource
  retentionPeriodInDaysForMedia: number | null
  retentionPeriodInDaysForDocuments: number | null
  retentionPeriodInDaysForTranscript: number | null
  retentionPeriodInDaysForMeeting: number | null
  maxSingleRetentionExtensionInDays: number | null
  maxTotalRetentionExtensionInDays: number | null
  /** The contract the usage dashboard measures against; null until support records it. */
  subscriptionStartDate: string | null
  subscriptionAllowanceHours: number | null
  /** Percentages of the allowance at which the client's administrators are emailed. */
  usageAlertThresholdPercentages: number[]
}

/**
 * The effective configuration of a team: its client's settings with the team's
 * overrides applied, and the merged settings unioned.
 */
export type ResolvedSettings = ClientSettings

/** The team-level overrides for settings whose null already means something on the client. */
export type TeamSettingOverrides = {
  assistantDisplayNameOverride: string | null
  llmModelOverride: string | null
  encryptionKeySourceOverride: EncryptionKeySource | null
  retentionPeriodInDaysForMediaOverride: number | null
  retentionPeriodInDaysForDocumentsOverride: number | null
  retentionPeriodInDaysForTranscriptOverride: number | null
  retentionPeriodInDaysForMeetingOverride: number | null
  maxSingleRetentionExtensionInDaysOverride: number | null
  maxTotalRetentionExtensionInDaysOverride: number | null
}

export const INHERITED_SETTING_NAMES = [
  'allowSignUp',
  'minutesVerboseness',
  'minutesAnonymization',
  'minutesPassive',
  'minutesNameFormat',
  'minutesDateFormat',
  'minutesTimeFormat',
  'minutesLanguage',
  'minutesAttribution',
  'anonymiseMemberSpeakers',
  'useTranscriptRefinement',
  'shareUrlOptOut',
  'transcriberInputLanguage',
  'transcriberOutputLanguage',
  'allowTranscriptDownload',
  'allowVideoDownload',
  'allowMinutesDownload',
  'allowOutlineDownload',
  'allowManualMeetingCreation',
  'allowMeetingUpload',
  'useVerificationDiscovery',
  'useAgendaTranscriptMapping',
  'allowBoardPackUploads',
  'allowAdminMeetingActions',
  'allowRetentionExtensionRequests',
  'softDeletionPeriodInDays',
  'isIntercomEnabled',
  'isSharingEnabled',
  'isCalendarSynchronizationEnabled',
  'recordingType',
  'defaultMeetingMedium',
  'sendSoftDeletionWarningEmail',
  'softDeletionWarningHours',
  'sendSoftDeletionConfirmationEmail',
  'sendHardDeletionConfirmationEmail',
  'sendAssistantAccessDeniedEmail',
  'sendUpcomingMeetingReminderEmail',
  'sendWeeklyDigestEmail',
  'sendConsentNotification',
  'joinMeetingInternal',
  'joinMeetingExternal',
  'freezeJoinSettings',
  'useSectionHeadings',
  'usePartialMinutesRegeneration',
  'allowRestrictedUsers',
  'allowMeetingEntities',
  'allowActionItems',
  'allowLlmSpeechRecovery',
  'sessionTimeoutInMinutes',
] as const

export const OVERRIDABLE_SETTING_NAMES = [
  'assistantDisplayName',
  'llmModel',
  'encryptionKeySource',
  'retentionPeriodInDaysForMedia',
  'retentionPeriodInDaysForDocuments',
  'retentionPeriodInDaysForTranscript',
  'retentionPeriodInDaysForMeeting',
  'maxSingleRetentionExtensionInDays',
  'maxTotalRetentionExtensionInDays',
] as const

export const CLIENT_ONLY_SETTING_NAMES = ['dashboardUrl', 'enabledMinutesLanguages', 'minutingAgents', 'useVirtualHostedStorageUrls'] as const

export const MERGED_SETTING_NAMES = ['contactEmails', 'context'] as const

export type InheritedSettingName = (typeof INHERITED_SETTING_NAMES)[number]
export type OverridableSettingName = (typeof OVERRIDABLE_SETTING_NAMES)[number]
export type MergedSettingName = (typeof MERGED_SETTING_NAMES)[number]

type ResolvableTeam = {
  client: ClientSettings
} & Partial<Record<InheritedSettingName, unknown>> &
  Partial<Record<`${OverridableSettingName}Override`, unknown>> &
  Partial<Record<MergedSettingName, string[]>>

/**
 * Mirrors the backend's resolveSettings for the places that hold a raw team rather than a
 * payload carrying `resolvedSettings`. Precedence is team override, then client value, with
 * the merged settings unioned.
 */
export function resolveSettings(team: ResolvableTeam): ResolvedSettings {
  const resolved: Record<string, unknown> = { ...team.client }
  for (const setting of INHERITED_SETTING_NAMES) {
    const override = team[setting]
    if (override !== null && override !== undefined) {
      resolved[setting] = override
    }
  }
  for (const setting of OVERRIDABLE_SETTING_NAMES) {
    const override = team[`${setting}Override`]
    if (override !== null && override !== undefined) {
      resolved[setting] = override
    }
  }
  for (const setting of MERGED_SETTING_NAMES) {
    resolved[setting] = [...new Set([...team.client[setting], ...(team[setting] ?? [])])]
  }
  return resolved as ResolvedSettings
}
